import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { deleteMaterial, getMaterials } from '../../services/materials';
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse';
import SetMaterialModal from './materialsSet';
import MaterialDetail from './materialDetail';
import { rsdFormat } from '../../utils/helper';
import OverlayLoader from '../../components/overlayLoader';
import ConfirmationModal from '../../components/modals/confirmation';
import { SkeletonRows } from '../../components/skeleton';
import { isUserAdmin } from '../../utils/roleHelpers';

const BoxIcon = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
);

const stockStatusBadge = (material, t) => {
    if (!material.trackStock) return null;
    if (material.currentStock <= 0)
        return <span style={{ fontSize: '10.5px', fontWeight: 800, letterSpacing: '.3px', color: 'var(--bad)', background: 'var(--bad-bg)', border: '1px solid var(--bad-bd)', borderRadius: '7px', padding: '2px 8px', flexShrink: 0 }}>{t('materialsPage.outOfStock')}</span>;
    if (material.currentStock <= material.minimumStock)
        return <span style={{ fontSize: '10.5px', fontWeight: 800, letterSpacing: '.3px', color: 'var(--warn)', background: 'var(--warn-bg)', border: '1px solid var(--warn-bd)', borderRadius: '7px', padding: '2px 8px', flexShrink: 0 }}>{t('materialsPage.lowStock')}</span>;
    return null;
};

const MaterialsPage = () => {
    const { t } = useTranslation();

    const [setMaterialModal, setSetMaterialModal] = useState(false)
    const [materials, setMaterials] = useState([])
    const [confirmationModal, setConfirmationModal] = useState(false)
    const [materialToEdit, setMaterialToEdit] = useState()
    const [materialToDelete, setMaterialToDelete] = useState()
    const [detailMaterial, setDetailMaterial] = useState(null)
    const [search, setSearch] = useState('')

    const admin = isUserAdmin()

    const { data, isFetching } = useQuery(['materials', search], () => getMaterials({ search: search || undefined, excludeDisabled: !admin }))
    const queryClient = useQueryClient()
    const { mutate, isLoading: isDeleting } = useMutation(deleteMaterial, {
        onSuccess: onMutateSuccess(() => {
            queryClient.invalidateQueries(['materials'])
        }, t('materialsPage.materialDeleted'))
    })

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            const list = result.data || result
            setMaterials(list.map(({ category, ...rest }) => ({
                categoryName: category?.name || t('materialsPage.uncategorised'),
                ...rest,
            })))
        })
    }, [data])

    const handleShowSetModal = (material) => { setMaterialToEdit(material); setSetMaterialModal(true) }
    const handleShowDetail = (material) => setDetailMaterial(material)
    const handleShowConfirmation = (material) => { setConfirmationModal(true); setMaterialToDelete(material) }
    const handleDelete = () => mutate(materialToDelete.id)

    const grouped = materials.reduce((acc, m) => {
        const cat = m.categoryName;
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(m);
        return acc;
    }, {});

    return (
        <div className="sbr">
            <ConfirmationModal display={confirmationModal} toggle={setConfirmationModal} title={t('materialsPage.deleteTitle')}
                message={t('materialsPage.deleteMessage')} confirmAction={handleDelete}
                confirmText={t('common.confirm')} cancelText={t('common.cancel')} />
            <SetMaterialModal display={setMaterialModal} toggle={setSetMaterialModal} materialId={materialToEdit?.id} />
            {detailMaterial && <MaterialDetail material={detailMaterial} onClose={() => setDetailMaterial(null)} />}

            <div className="dhead">
                <div>
                    <div className="ttl">{t('materialsPage.titleFull')}</div>
                    <div className="sub">{t('materialsPage.subtitle')}</div>
                </div>
                <div className="act">
                    <div className="hsearch">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                        <input type="search" placeholder={t('materialsPage.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    {admin && (
                        <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => handleShowSetModal()}>
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                            </svg>
                            {t('materialsPage.addMaterial')}
                        </button>
                    )}
                </div>
            </div>

            {isFetching && materials.length === 0 ? (
                <div className="card-sbr">
                    <SkeletonRows rows={6} widths={['16%', '24%', '16%', '16%', '14%']} />
                </div>
            ) : materials.length === 0 ? (
                <div className="card-sbr">
                    <div className="sbr-empty">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                        </svg>
                        {t('materialsPage.noMaterials')}
                    </div>
                </div>
            ) : (
                Object.entries(grouped).map(([category, catMaterials]) => (
                    <div key={category}>
                        <div className="daygroup">
                            <svg className="ic" style={{ width: '15px', height: '15px', color: 'var(--brand)' }}
                                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                            </svg>
                            <span className="d">{category}</span>
                            <span className="dot"></span>
                            <span className="meta">{t('materialsPage.material', { count: catMaterials.length })}</span>
                        </div>

                        <div className="card-sbr" style={{ padding: 0, overflow: 'hidden' }}>
                            <div style={{ padding: '2px 16px' }}>
                                {catMaterials.map((material, idx) => (
                                    <div key={material.id ?? idx} className="row-sbr" style={{ cursor: 'pointer' }} onClick={() => handleShowDetail(material)}>
                                        <div style={{
                                            width: '42px', height: '42px', borderRadius: '13px', flexShrink: 0,
                                            background: 'var(--brand-50)', border: '1px solid var(--brand-100)',
                                            color: 'var(--brand-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            overflow: 'hidden',
                                        }}>
                                            {material.photoUrl
                                                ? <img src={material.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                : <BoxIcon />
                                            }
                                        </div>

                                        <div className="info">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span className="nm">{material.name}</span>
                                                {material.sku && (
                                                    <span style={{
                                                        fontSize: '10.5px', fontWeight: 800, letterSpacing: '.3px',
                                                        color: 'var(--muted)', background: 'var(--surface)',
                                                        border: '1px solid var(--line)', borderRadius: '7px', padding: '2px 8px',
                                                        flexShrink: 0,
                                                    }}>
                                                        {material.sku}
                                                    </span>
                                                )}
                                                {stockStatusBadge(material, t)}
                                                {material.disabled && (
                                                    <span style={{
                                                        fontSize: '10.5px', fontWeight: 800, letterSpacing: '.3px',
                                                        color: 'var(--bad)', background: 'var(--bad-bg)',
                                                        border: '1px solid var(--bad-bd)', borderRadius: '7px', padding: '2px 8px',
                                                        flexShrink: 0,
                                                    }}>
                                                        {t('common.disabled')}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="sub">
                                                {rsdFormat(material.sellingPrice)} {t('materialsPage.perUnit', { unit: material.unit || t('common.unit') })}
                                                {material.trackStock && <> · {t('materialsPage.inStockCount', { count: material.currentStock })}</>}
                                            </div>
                                        </div>

                                        {admin && (
                                            <div className="rt" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <button title={t('common.edit')} className="btn-ghost-sbr btn-sbr btn-sm-sbr"
                                                    style={{ padding: '6px 8px', color: 'var(--brand)' }}
                                                    onClick={(e) => { e.stopPropagation(); handleShowSetModal(material) }}>
                                                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                    </svg>
                                                </button>
                                                <button title={t('common.delete')} className="btn-ghost-sbr btn-sbr btn-sm-sbr"
                                                    style={{ padding: '6px 8px', color: 'var(--bad)' }}
                                                    onClick={(e) => { e.stopPropagation(); handleShowConfirmation(material) }}>
                                                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                                                    </svg>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))
            )}

            {((isFetching && materials.length > 0) || isDeleting) && (
                <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 10 }}>
                    <OverlayLoader />
                </div>
            )}
        </div>
    );
};

export default MaterialsPage;
